/**
 * 前台 SPA 应用导航栏 (Refactored)
 * - 使用拆分的子组件提高性能和维护性
 * - 使用 useTheme Hook 管理主题
 * - 优化重渲染和加载体验
 */

import React, { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useNavSettings } from "@/hooks/useNavSettings";
import { useOperationRegion } from "@/hooks/useOperationRegion";
import { useCurrentUser, useUserLoading } from "@/stores/userStore";
import { useTheme } from "@/hooks/useTheme";

// 子组件
import { NavLogo } from "./nav/NavLogo";
import { UserMenu } from "./nav/UserMenu";
import { DesktopMenu } from "./nav/DesktopMenu";
import { MobileMenu } from "./nav/MobileMenu";

interface AppNavProps {
  // 注意：不再强烈依赖 props.user，优先使用 userStore
  user?: {
    name: string;
    account_id?: string;
    avatar_url?: string | null;
    unread?: number;
    points?: number;
    checked_in?: boolean;
  } | null;
}

export default function AppNav({ user: propUser }: AppNavProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // 1. 状态管理中心
  const currentUser = useCurrentUser();
  const isUserLoading = useUserLoading();
  const { region, loading: isRegionLoading } = useOperationRegion();
  const { resolvedTheme, themeSetting, setTheme } = useTheme();
  const { data: navSettings, isLoading: isNavSettingsLoading } =
    useNavSettings();

  // 2. 统一加载状态判断
  // 只有当导航配置、运营区域、用户信息（如果正在检查中）都准备好，才进行“统一揭晓”
  const isFullyLoaded =
    !isNavSettingsLoading && !isRegionLoading && !isUserLoading;

  // 统一未读消息数量来源
  const unreadCount = currentUser?.unread ?? propUser?.unread ?? 0;
  const displayUser = currentUser || propUser;

  // 3. 多端交互逻辑
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleEsc = (e: KeyboardEvent) =>
      e.key === "Escape" && setIsMobileMenuOpen(false);
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* 移动端遮罩 */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden bg-background/30 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <nav className="fixed top-0 inset-x-0 z-50 bg-background/30 backdrop-blur-xs shadow-xs shadow-black/5 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent dark:before:via-white/10">
        <div
          className={cn(
            "relative flex justify-between items-center h-16 px-4 max-w-7xl mx-auto lg:grid lg:grid-cols-3 transition-opacity duration-300",
            isFullyLoaded ? "opacity-100" : "opacity-0 pointer-events-none",
          )}
        >
          {/* 左侧：Logo 或返回按钮（由 NavBackContext 控制） */}
          <div className="flex items-center">
            <NavLogo region={region} theme={resolvedTheme} />
          </div>

          {/* 中间：桌面端导航列表 */}
          <DesktopMenu navSettings={navSettings} isNavReady={isFullyLoaded} />

          {/* 右侧：用户操作区 */}
          <div className="flex items-center gap-2 justify-self-end">
            {displayUser ? (
              <UserMenu
                user={{
                  name: displayUser.name,
                  account_id: displayUser.account_id,
                  avatar_url: displayUser.avatar_url,
                  points: displayUser.points,
                  checked_in: displayUser.checked_in,
                }}
                unreadCount={unreadCount}
                themeSetting={themeSetting}
                setTheme={setTheme}
              />
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  asChild
                  className="text-foreground hover:text-foreground"
                >
                  <Link to="/login">{t("nav.login", "登录")}</Link>
                </Button>
                <Button size="sm" asChild>
                  <Link to="/register">{t("nav.register", "注册")}</Link>
                </Button>
              </div>
            )}

            {/* 移动端汉堡键 */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setIsMobileMenuOpen((o) => !o)}
            >
              {isMobileMenuOpen ? (
                <X className="size-5" />
              ) : (
                <Menu className="size-5" />
              )}
            </Button>
          </div>
        </div>

        {/* 移动端下拉面板 */}
        <MobileMenu
          navSettings={navSettings}
          isOpen={isMobileMenuOpen && isFullyLoaded}
        />
      </nav>

      {/* 顶部占位 */}
      <div className="h-16" />
    </>
  );
}
